import {Injectable, Injector} from '@angular/core';
import {defer, Observable} from 'rxjs';
import {map, shareReplay, switchMap} from 'rxjs/operators';
import {ActivityTrackerPublisher, LogEntry} from 'app/original-services/activity-tracker.service';

/**
 * Root-level `ActivityTrackerPublisher` that loads the real GetStream publisher on first use.
 *
 * `ActivityTrackerService` is `providedIn: 'root'` and is injected by services that are on the eager
 * path (`CompanyService`, `BankAccountsService`, `AuthenticationService`…), so the publisher token it
 * depends on MUST resolve from the root injector — providing it from the lazily loaded
 * `AppShellModule` throws NG0201, because root services cannot see a lazy module's providers.
 *
 * But binding the token straight to `ActivityTrackerGetStream` in `AppModule` puts the whole GetStream
 * client into the eager bundle that the sign-in page downloads. This proxy is the middle ground: the
 * token resolves at the root, and the GetStream implementation arrives through a dynamic `import()`
 * the first time something is actually logged — which only happens inside the authenticated app.
 *
 * Both methods return Observables, and every caller subscribes (`ActivityTrackerService.writeToLog`
 * does so immediately), so deferring the real call until the module has loaded preserves behaviour.
 * The resolved publisher is the same `providedIn: 'root'` `ActivityTrackerGetStream` singleton that
 * authenticated components inject directly.
 */
@Injectable({providedIn: 'root'})
export class LazyActivityTrackerPublisher extends ActivityTrackerPublisher {

    /** Overridable so specs can supply a publisher without loading the GetStream chunk. */
    protected load: () => Promise<ActivityTrackerPublisher> = () =>
        import('app/original-services/activity-tracker-getstream.service')
            .then(m => this.injector.get(m.ActivityTrackerGetStream));

    // shareReplay: import and resolve once, then hand every later call the same instance. A failed
    // import is not cached (RxJS 7 shareReplay resubscribes after an error), so a later log retries.
    private readonly publisher$: Observable<ActivityTrackerPublisher> = defer(() => this.load()).pipe(
        shareReplay({bufferSize: 1, refCount: false})
    );

    constructor(private injector: Injector) {
        super();
    }

    log(record: LogEntry): Observable<boolean> {
        return this.publisher$.pipe(switchMap(publisher => publisher.log(record)));
    }

    logAsObservable(record: LogEntry): Observable<Promise<any>> {
        return this.publisher$.pipe(switchMap(publisher => publisher.logAsObservable(record)));
    }
}
